import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    Plus, Search, Archive, RotateCcw, ShieldCheck, Pencil, Loader2, Users2,
} from "lucide-react";
import { toast } from "sonner";
import { Reveal, RevealItem } from "../components/Reveal";
import { MagneticButton } from "../components/MagneticButton";
import { getJSON, postJSON, delJSON } from "../lib/api";
import { usePageMeta } from "../lib/usePageMeta";
import { useAuthStore } from "../state/authStore";
import { NewClientModal } from "../components/ipam/NewClientModal";
import { Paywall } from "../components/ipam/Paywall";
import { SEO } from "../components/SEO";

/** Format an ISO date as "Mar 8, 2026" (locale-aware short). */
const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
        return new Intl.DateTimeFormat(undefined, {
            year: "numeric", month: "short", day: "numeric",
        }).format(new Date(iso));
    } catch {
        return "—";
    }
};

export const Clients = () => {
    usePageMeta({ title: "Clients — Cybersecurity Forefront" });
    const user = useAuthStore((s) => s.user);

    const [quota, setQuota] = useState(null);
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [paywalled, setPaywalled] = useState(false);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("active");
    const [showNewModal, setShowNewModal] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const [q, list] = await Promise.all([
                getJSON("/ipam/quota"),
                getJSON(`/ipam/clients?status=${statusFilter}${search ? `&q=${encodeURIComponent(search)}` : ""}`),
            ]);
            setQuota(q);
            setItems(list.items || []);
            setTotal(list.total || 0);
            setPaywalled(false);
        } catch (e) {
            if (e.status === 402) {
                setPaywalled(true);
            } else {
                toast.error(e.message || "Failed to load clients");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

    // Debounced search
    useEffect(() => {
        const t = setTimeout(load, 350);
        return () => clearTimeout(t);
        // eslint-disable-next-line
    }, [search]);

    const archive = async (id, name) => {
        if (!window.confirm(`Archive “${name}”? You can restore it later from the Archived tab.`)) return;
        try {
            await delJSON(`/ipam/clients/${id}`);
            toast.success("Client archived");
            load();
        } catch (e) {
            toast.error(e.message || "Archive failed");
        }
    };

    const restore = async (id) => {
        try {
            await postJSON(`/ipam/clients/${id}/restore`, {});
            toast.success("Client restored");
            load();
        } catch (e) {
            if (e.status === 402) toast.error(e.payload?.detail?.message || "Client cap reached");
            else toast.error(e.message || "Restore failed");
        }
    };

    const capLabel = useMemo(() => {
        if (!quota) return "—";
        if (quota.cap === null) return `${quota.used} clients`;
        return `${quota.used} / ${quota.cap}`;
    }, [quota]);

    const atCap = quota && quota.cap !== null && quota.remaining === 0;

    if (paywalled) return <Paywall />;

    return (
        <div className="pt-24 md:pt-28 pb-12 mx-auto max-w-7xl px-5 md:px-10" data-testid="clients-page">
            <SEO noindex title="Clients" canonicalPath="/clients" />
            <Reveal>
                <p className="text-[10.5px] font-mono uppercase tracking-[0.32em] text-accent-cyan">Client Data Suite</p>
                <div className="mt-3 flex items-end justify-between gap-4 flex-wrap">
                    <div>
                        <h1 className="font-display font-semibold text-4xl sm:text-5xl tracking-tight leading-[1.05]" data-testid="clients-title">
                            My Clients
                        </h1>
                        <p className="mt-2 text-text-muted text-[14px]">
                            One workspace per client — IPAM, NIS2, and (soon) hardware inventory and ZeroConf reuse.
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        <span
                            className={`inline-flex items-center gap-1.5 text-[11.5px] font-mono uppercase tracking-[0.22em] px-2.5 py-1 rounded-md border ${
                                atCap
                                    ? "border-accent-amber/40 text-accent-amber bg-accent-amber/[0.07]"
                                    : "border-white/[0.08] text-text-muted bg-elevated/40"
                            }`}
                            data-testid="clients-quota-badge"
                            title={quota?.tier === "msp" ? "MSP — unlimited" : `${quota?.tier} plan${quota?.is_founding_member ? " · Founding Member (+5 cap)" : ""}`}
                        >
                            <Users2 className="w-3 h-3" />
                            {capLabel}
                        </span>
                        <MagneticButton
                            type="button"
                            onClick={() => setShowNewModal(true)}
                            disabled={atCap}
                            className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-[13px] font-medium transition-all duration-200 ${
                                atCap
                                    ? "bg-elevated/40 border border-white/[0.06] text-text-dim cursor-not-allowed"
                                    : "bg-accent-cyan text-canvas hover:shadow-glow-cyan"
                            }`}
                            ariaLabel="New Client"
                            data-testid="clients-new-btn"
                            title={atCap ? "Cap reached — upgrade to MSP for unlimited" : "Create a new client workspace"}
                        >
                            <Plus className="w-3.5 h-3.5" /> New Client
                        </MagneticButton>
                    </div>
                </div>
            </Reveal>

            {/* Filters */}
            <div className="mt-8 flex flex-wrap items-center gap-3" data-testid="clients-toolbar">
                <div className="relative flex-1 min-w-[220px] max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-text-dim" />
                    <input
                        type="text"
                        placeholder="Search name or industry…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full pl-9 pr-3 py-2 rounded-md bg-surface/60 border border-white/[0.06] text-text-primary text-[13px] placeholder:text-text-dim focus:outline-none focus:border-accent-cyan/40"
                        data-testid="clients-search"
                    />
                </div>
                <div className="inline-flex rounded-md border border-white/[0.06] bg-surface/60 p-0.5" data-testid="clients-status-tabs">
                    {["active", "archived"].map((s) => (
                        <button
                            key={s}
                            type="button"
                            onClick={() => setStatusFilter(s)}
                            className={`px-3 py-1.5 text-[12px] font-medium rounded-[5px] transition-colors ${
                                statusFilter === s
                                    ? "bg-elevated text-text-primary"
                                    : "text-text-muted hover:text-text-primary"
                            }`}
                            data-testid={`clients-status-${s}`}
                        >
                            {s === "active" ? "Active" : "Archived"}
                        </button>
                    ))}
                </div>
            </div>

            {/* Body */}
            {loading ? (
                <div className="mt-12 flex items-center justify-center text-text-muted">
                    <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading clients…
                </div>
            ) : items.length === 0 ? (
                <EmptyState onCreate={() => setShowNewModal(true)} statusFilter={statusFilter} atCap={atCap} />
            ) : (
                <Reveal stagger className="mt-8 grid gap-3">
                    {items.map((c) => (
                        <RevealItem key={c.id}>
                            <div
                                className="group rounded-xl border border-white/[0.06] bg-surface/50 hover:border-accent-cyan/30 transition-colors duration-200 p-4 md:p-5 flex items-center gap-4 flex-wrap"
                                data-testid={`client-card-${c.slug}`}
                            >
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <Link
                                            to={`/clients/${c.slug}`}
                                            className="font-display font-semibold text-lg text-text-primary tracking-tight hover:text-accent-cyan transition-colors truncate"
                                            data-testid={`client-name-${c.slug}`}
                                        >
                                            {c.name}
                                        </Link>
                                        {c.nis2_in_scope && (
                                            <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-[0.22em] px-1.5 py-0.5 rounded border border-accent-mint/30 text-accent-mint">
                                                <ShieldCheck className="w-2.5 h-2.5" /> NIS2
                                            </span>
                                        )}
                                        {c.status === "archived" && (
                                            <span className="text-[10px] font-mono uppercase tracking-[0.22em] px-1.5 py-0.5 rounded border border-white/[0.1] text-text-dim">
                                                Archived
                                            </span>
                                        )}
                                    </div>
                                    <div className="mt-1.5 flex items-center gap-3 text-[12px] text-text-muted flex-wrap">
                                        {c.industry && <span>{c.industry}</span>}
                                        <span className="text-text-dim">·</span>
                                        <span>{c.subnet_count} {c.subnet_count === 1 ? "subnet" : "subnets"}</span>
                                        <span className="text-text-dim">·</span>
                                        <span>Updated {fmtDate(c.updated_at)}</span>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 opacity-70 group-hover:opacity-100 transition-opacity">
                                    {c.status === "active" ? (
                                        <>
                                            <Link
                                                to={`/clients/${c.slug}`}
                                                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-white/[0.06] bg-elevated/40 text-[12px] text-text-muted hover:text-accent-cyan hover:border-accent-cyan/30 transition-colors"
                                                data-testid={`client-edit-${c.slug}`}
                                            >
                                                <Pencil className="w-3 h-3" /> Edit
                                            </Link>
                                            <button
                                                type="button"
                                                onClick={() => archive(c.id, c.name)}
                                                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-white/[0.06] bg-elevated/40 text-[12px] text-text-muted hover:text-accent-amber hover:border-accent-amber/30 transition-colors"
                                                data-testid={`client-archive-${c.slug}`}
                                            >
                                                <Archive className="w-3 h-3" /> Archive
                                            </button>
                                        </>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={() => restore(c.id)}
                                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-accent-mint/30 text-[12px] text-accent-mint hover:bg-accent-mint/[0.08] transition-colors"
                                            data-testid={`client-restore-${c.slug}`}
                                        >
                                            <RotateCcw className="w-3 h-3" /> Restore
                                        </button>
                                    )}
                                </div>
                            </div>
                        </RevealItem>
                    ))}
                </Reveal>
            )}

            {showNewModal && (
                <NewClientModal
                    onClose={() => setShowNewModal(false)}
                    onCreated={(c) => {
                        setShowNewModal(false);
                        toast.success(`Created “${c.name}”`);
                        load();
                    }}
                />
            )}

            <p className="mt-10 text-[11px] text-text-dim">
                Total: <span className="text-text-muted">{total}</span> · plan:{" "}
                <span className="text-text-muted">{quota?.tier || "—"}</span>
                {quota?.is_founding_member && (
                    <> · <span className="text-accent-mint">Founding Member +5</span></>
                )}
            </p>
        </div>
    );
};

const EmptyState = ({ onCreate, statusFilter, atCap }) => {
    if (statusFilter === "archived") {
        return (
            <div className="mt-16 text-center" data-testid="clients-empty-archived">
                <p className="text-text-muted">No archived clients.</p>
            </div>
        );
    }
    return (
        <div className="mt-16 text-center" data-testid="clients-empty">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl border border-white/[0.08] bg-surface/60 mb-4">
                <Users2 className="w-5 h-5 text-text-dim" />
            </div>
            <h2 className="font-display font-semibold text-2xl text-text-primary tracking-tight">
                Spin up your first client workspace
            </h2>
            <p className="mt-2 text-text-muted text-[13px] max-w-md mx-auto">
                Track subnets, NIS2 scope, contacts, and (soon) hardware inventory under one roof for each customer you support.
            </p>
            <MagneticButton
                type="button"
                onClick={onCreate}
                disabled={atCap}
                className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-md bg-accent-cyan text-canvas text-[13px] font-medium hover:shadow-glow-cyan disabled:opacity-40"
                ariaLabel="Create your first client"
                data-testid="clients-empty-cta"
            >
                <Plus className="w-3.5 h-3.5" /> Create your first client
            </MagneticButton>
        </div>
    );
};

export default Clients;
